import { z } from "zod";
import type { ObligationContractDto, Sourcing } from "./contracts.js";
import { OBLIGATION_BASES, OBLIGATION_KINDS, type ObligationBasis, type ObligationKind } from "./library.js";
import type { MetrelRecordData } from "./metrel.js";
import type { IntakeStatus } from "./intake.js";

/**
 * Keuringen bij assets (docs/ASSETS-MJOP.md, "Keuringen bij assets"). Status wordt berekend, nooit
 * opgeslagen (besluit 11); deze regels gelden in server, web en rapport.
 */
export const OBLIGATION_STATUSES = ["ok", "soon", "overdue", "unknown", "na"] as const;
export type ObligationStatus = (typeof OBLIGATION_STATUSES)[number];
export const OBLIGATION_STATUS_LABELS: Record<ObligationStatus, string> = {
  ok: "Op orde",
  soon: "Binnenkort",
  overdue: "Verlopen",
  unknown: "Onbekend",
  na: "Niet van toepassing",
};
/** Slechtste eerst; Niet van toepassing telt nergens mee. */
const RANK: Record<ObligationStatus, number> = { overdue: 4, soon: 3, unknown: 2, ok: 1, na: 0 };

/** Grondslag zoals de app hem toont. Alleen Wettelijk geeft spoed bij overschrijding. */
export const OBLIGATION_BASIS_APP_LABELS: Record<ObligationBasis, string> = {
  wettelijk: "Wettelijk verplicht",
  zorgplicht: "Zorgplicht — richtlijn",
  verzekeraar: "Eis verzekeraar",
  eigen: "Eigen afspraak",
};

export const RECORD_RESULTS = ["goedgekeurd", "met_opmerkingen", "afgekeurd"] as const;
export type RecordResult = (typeof RECORD_RESULTS)[number];
export const RECORD_RESULT_LABELS: Record<RecordResult, string> = {
  goedgekeurd: "Goedgekeurd",
  met_opmerkingen: "Goedgekeurd met opmerkingen",
  afgekeurd: "Afgekeurd",
};

/**
 * Woorden per soort uitvoering. Keuring, inspectie, controle en onderhoud delen één model (verplichting
 * met interval, historie en status), maar een keuring geeft een oordeel en onderhoud wordt uitgevoerd.
 * De opgeslagen uitkomst blijft `RecordResult`; alleen de woorden verschillen.
 */
export interface ObligationKindText {
  /** "Keuring", "Onderhoud" … als zelfstandig naamwoord. */
  noun: string;
  /** Knop en venstertitel: "Keuring vastleggen". */
  record: string;
  /** Taak vóór de vervaldatum: "Keuring plannen". */
  plan: string;
  /** Uitkomsten in volgorde van `RECORD_RESULTS`. */
  results: Record<RecordResult, string>;
  /** Titel van de bevinding bij een afkeuring: "Keuring afgekeurd". */
  failedTitle: string;
  /** Status als de laatste uitkomst `afgekeurd` was. */
  failedStatus: string;
  /** Bewijsstuk bij het vastleggen. */
  document: string;
  /** Veld certificering tonen (alleen bij een oordeel door een deskundige). */
  certification: boolean;
}

export const OBLIGATION_KIND_TEXT: Record<ObligationKind, ObligationKindText> = {
  keuring: {
    noun: "Keuring",
    record: "Keuring vastleggen",
    plan: "Keuring plannen",
    results: RECORD_RESULT_LABELS,
    failedTitle: "Keuring afgekeurd",
    failedStatus: "Afgekeurd",
    document: "Certificaat",
    certification: true,
  },
  inspectie: {
    noun: "Inspectie",
    record: "Inspectie vastleggen",
    plan: "Inspectie plannen",
    results: { goedgekeurd: "In orde", met_opmerkingen: "In orde, met opmerkingen", afgekeurd: "Gebreken gevonden" },
    failedTitle: "Gebreken bij inspectie",
    failedStatus: "Gebreken",
    document: "Rapport",
    certification: true,
  },
  controle: {
    noun: "Controle",
    record: "Controle vastleggen",
    plan: "Controle plannen",
    results: { goedgekeurd: "In orde", met_opmerkingen: "In orde, met opmerkingen", afgekeurd: "Niet in orde" },
    failedTitle: "Controle niet in orde",
    failedStatus: "Niet in orde",
    document: "Bijlage",
    certification: false,
  },
  onderhoud: {
    noun: "Onderhoud",
    record: "Onderhoud vastleggen",
    plan: "Onderhoud plannen",
    results: {
      goedgekeurd: "Uitgevoerd",
      met_opmerkingen: "Uitgevoerd, met opmerkingen",
      afgekeurd: "Probleem gevonden",
    },
    failedTitle: "Probleem bij onderhoud",
    failedStatus: "Probleem",
    document: "Werkbon",
    certification: false,
  },
  overig: {
    noun: "Uitvoering",
    record: "Uitvoering vastleggen",
    plan: "Uitvoering plannen",
    results: {
      goedgekeurd: "Uitgevoerd",
      met_opmerkingen: "Uitgevoerd, met opmerkingen",
      afgekeurd: "Probleem gevonden",
    },
    failedTitle: "Probleem bij uitvoering",
    failedStatus: "Probleem",
    document: "Bijlage",
    certification: false,
  },
};

/** Statuslabel; na een afkeuring "Afgekeurd" (of "Niet in orde" …) in plaats van "Verlopen". */
export const obligationStatusLabel = (
  status: ObligationStatus,
  kind: ObligationKind,
  lastResult: RecordResult | null | undefined,
) =>
  status === "overdue" && lastResult === "afgekeurd"
    ? OBLIGATION_KIND_TEXT[kind].failedStatus
    : OBLIGATION_STATUS_LABELS[status];

/** Dagen voor de vervaldatum: Binnenkort en de taak *Keuring plannen* (per organisatie instelbaar). */
export const DEFAULT_REMINDER_DAYS = 60;

const DAY = 86_400_000;
const parse = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return Date.UTC(y!, m! - 1, d!);
};
const fmt = (t: number) => new Date(t).toISOString().slice(0, 10);

/** Vandaag als JJJJ-MM-DD (Nederlandse tijd). */
export const todayIso = (now = new Date()) =>
  new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Amsterdam" }).format(now);

/** Datum plus maanden, op de laatste dag als die maand korter is: 31 jan + 1 = 28/29 feb. */
export function addMonths(iso: string, months: number): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const first = new Date(Date.UTC(y!, m! - 1 + months, 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  return fmt(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), Math.min(d!, last)));
}

export const addDays = (iso: string, days: number) => fmt(parse(iso) + days * DAY);
/** Dagen van `from` tot `to` (negatief als `to` eerder is). */
export const daysBetween = (from: string, to: string) => Math.round((parse(to) - parse(from)) / DAY);

/** Volgende vervaldatum na een keuring; null zonder vaste termijn. Interval in maanden of weken. */
export const nextDue = (performedOn: string, intervalMonths: number | null, intervalWeeks: number | null = null) =>
  intervalWeeks
    ? addDays(performedOn, 7 * intervalWeeks)
    : intervalMonths
      ? addMonths(performedOn, intervalMonths)
      : null;

export interface StatusInput {
  notApplicable: boolean;
  nextDueOn: string | null;
  lastResult: RecordResult | null;
}

/**
 * Op orde (> X dagen), Binnenkort (≤ X dagen, vandaag = vervaldatum telt mee), Verlopen (voorbij of
 * laatste keuring afgekeurd), Onbekend (geen datum).
 */
export function obligationStatus(
  o: StatusInput,
  today: string,
  soonDays: number = DEFAULT_REMINDER_DAYS,
): ObligationStatus {
  if (o.notApplicable) return "na";
  if (o.lastResult === "afgekeurd") return "overdue";
  if (!o.nextDueOn) return "unknown";
  const days = daysBetween(today, o.nextDueOn);
  if (days < 0) return "overdue";
  return days <= soonDays ? "soon" : "ok";
}

/** Slechtste status van een asset of gebouw; null als er niets meetelt. */
export function worstStatus(list: readonly ObligationStatus[]): ObligationStatus | null {
  let out: ObligationStatus | null = null;
  for (const s of list) if (s !== "na" && (!out || RANK[s] > RANK[out])) out = s;
  return out;
}

/** "nog 23 dagen", "vandaag", "3 weken verlopen", "nog 14 maanden". */
export function dueText(nextDueOn: string | null, today: string): string {
  if (!nextDueOn) return "geen datum";
  const d = daysBetween(today, nextDueOn);
  if (d === 0) return "vandaag";
  const n = Math.abs(d);
  // Vooruit in dagen tot twee maanden (de planner telt af); achteraf in weken vanaf twee weken.
  const months = Math.round(n / 30.44);
  const span =
    n < (d > 0 ? 61 : 14)
      ? `${n} ${n === 1 ? "dag" : "dagen"}`
      : n < 61
        ? `${Math.round(n / 7)} weken`
        : `${months} ${months === 1 ? "maand" : "maanden"}`;
  return d > 0 ? `nog ${span}` : `${span} verlopen`;
}

// ── API ──

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Datum als JJJJ-MM-DD");
const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => v || null);

const obligationFields = {
  name: z.string().trim().min(1, "Naam is verplicht").max(200),
  kind: z.enum(OBLIGATION_KINDS),
  intervalMonths: z.number().int().min(1).max(600).nullable(),
  /** Wekelijks werk: interval in weken (dan is `intervalMonths` leeg). */
  intervalWeeks: z.number().int().min(1).max(52).nullish(),
  costCents: z.number().int().min(0).max(100_000_000).nullish(),
  priceLevelYear: z.number().int().min(1900).max(2200).nullish(),
  basis: z.enum(OBLIGATION_BASES),
  /** Vrije tekst; met een bedrijf of persoon uit het adresboek zet de server hier de weergavenaam. */
  performer: text(200),
  performerCompanyId: z.uuid().nullish(),
  performerPersonId: z.uuid().nullish(),
  appliesWhen: text(300),
  sourceTitle: text(200),
  sourceUrl: text(500),
  notApplicable: z.boolean().default(false),
  overrideReason: text(500),
  /** Startpunt: datum laatste keuring (onbekend mag). */
  lastPerformedOn: isoDate.nullish(),
  /** Leeg = laatste keuring + interval. */
  nextDueOn: isoDate.nullish(),
  /** Laatste jaar van uitvoering (MJOP); leeg = doorlopend. */
  endYear: z.number().int().min(1900).max(2200).nullish(),
  reminderDays: z.number().int().min(0).max(365).nullish(),
};
const reasonRequired = (o: { notApplicable?: boolean; overrideReason?: string | null }) =>
  !o.notApplicable || Boolean(o.overrideReason);
const reasonIssue = { message: "Geef een korte reden", path: ["overrideReason"] };

export const assetObligationCreateSchema = z
  .object({
    id: z.uuid(),
    ...obligationFields,
    basis: z.enum(OBLIGATION_BASES).default("eigen"),
  })
  .refine(reasonRequired, reasonIssue);
export type AssetObligationCreate = z.input<typeof assetObligationCreateSchema>;

export const assetObligationUpdateSchema = z.object(obligationFields).partial().refine(reasonRequired, reasonIssue);
export type AssetObligationUpdate = z.input<typeof assetObligationUpdateSchema>;

export const obligationRecordCreateSchema = z.object({
  id: z.uuid(),
  performedOn: isoDate,
  /** Vrije naam; met een bedrijf of persoon uit het adresboek vult de server de weergavenaam zelf. */
  performedByCompany: text(200),
  performedByCompanyId: z.uuid().nullish(),
  performedByPersonId: z.uuid().nullish(),
  /** Bijv. "SCIOS Scope 8". */
  certification: text(200),
  result: z.enum(RECORD_RESULTS),
  certificateFileId: z.uuid().nullish(),
  /** Leeg = keuringsdatum + interval; aan te passen. */
  nextDueOn: isoDate.nullish(),
  notes: text(2000),
  /** Gebreken als losse regels; komen als submeldingen in de Inbox (alleen bij opmerkingen of een afkeuring). */
  defects: z.array(z.string().trim().min(1).max(500)).max(20).optional(),
});
export type ObligationRecordCreate = z.input<typeof obligationRecordCreateSchema>;
/** Certificaat mag later. */
export const obligationRecordUpdateSchema = z.object({
  certificateFileId: z.uuid().nullable().optional(),
  notes: text(2000).optional(),
});

export interface ObligationRecordDto {
  id: string;
  performedOn: string;
  performedByCompany: string | null;
  /** Uitvoerder uit het adresboek; `performedByCompany` is dan de weergavenaam. */
  performedByCompanyId: string | null;
  performedByPersonId: string | null;
  certification: string | null;
  result: RecordResult;
  certificateFileId: string | null;
  nextDueOn: string | null;
  notes: string | null;
  finding: { id: string; code: string } | null;
  /** Alle bevindingen uit deze registratie (ook uit de gebreken in de Inbox). */
  findings: { id: string; code: string }[];
  /** Melding in de Inbox bij opmerkingen of een afkeuring (issue #151). */
  intake: { id: string; code: string; status: IntakeStatus } | null;
  /** Metingen uit de meter (Metrel-import); null bij een keuring die met de hand is vastgelegd. */
  measurements: MetrelRecordData | null;
  /** Metrel-import (keuronde) waar deze keuring uit komt. */
  importId: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface AssetObligationDto {
  id: string;
  /** null = bij het gebouw zelf, zonder asset (bv. gevelreiniging). */
  assetId: string | null;
  buildingId: string;
  name: string;
  kind: ObligationKind;
  intervalMonths: number | null;
  intervalWeeks: number | null;
  /** null zonder MJOP-recht. */
  costCents: number | null;
  priceLevelYear: number | null;
  basis: ObligationBasis;
  /** Weergavenaam: vrije tekst of de vaste uitvoerder uit het adresboek. */
  performer: string | null;
  performerCompanyId: string | null;
  performerPersonId: string | null;
  appliesWhen: string | null;
  sourceTitle: string | null;
  sourceUrl: string | null;
  notApplicable: boolean;
  overrideReason: string | null;
  lastPerformedOn: string | null;
  nextDueOn: string | null;
  endYear: number | null;
  reminderDays: number | null;
  status: ObligationStatus;
  lastRecord: ObligationRecordDto | null;
  /** Onderhoudscontract dat deze uitvoering dekt (alleen met de module Contracten; docs/CONTRACTEN.md). */
  contract?: ObligationContractDto | null;
  /** Bewust zonder contract: eigen uitvoering of los inkopen. */
  sourcing?: Sourcing | null;
}

export interface AssetObligationsResponse {
  obligations: (AssetObligationDto & { records: ObligationRecordDto[] })[];
  status: ObligationStatus | null;
  /** Gaan opmerkingen en afkeuringen naar de Inbox (module aan bij de organisatie)? Ontbreekt in oude caches. */
  inbox?: boolean;
}

/** Eén regel in de lijst per gebouw of organisatie (planner). */
export interface ObligationListRow extends AssetObligationDto {
  /** null = bij het gebouw zelf. */
  asset: {
    id: string;
    code: string;
    name: string;
    elementCode: string | null;
    /** Waar de asset staat, zodat de keuringenlijst laat zien om welke het gaat. */
    floorName: string | null;
    roomName: string | null;
  } | null;
  building: { id: string; code: string; name: string };
}
